import { useCallback, useEffect, useState } from 'react';
import type { Artifact, ArtifactKind } from '@shared/protocol';
import { artifactUrl, extensionOf, fileRef } from '../lib/files';
import { ago, formatSize } from './FilesPanel';
import { Markdown } from './Markdown';
import { useConfirm } from './ConfirmDialog';
import { AtIcon, BackIcon, DownloadIcon, EnterIcon, FileIcon, OpenIcon, TrashIcon } from './icons';

/** Which artifact is open, and at which version; no version means the latest. */
export interface ArtifactView {
  id: string;
  version?: number;
}

interface Props {
  roomId: string;
  artifacts: Artifact[];
  view?: ArtifactView;
  onView: (view: ArtifactView | undefined) => void;
  /** Puts a reference to the artifact's working file into the message being written. */
  onReference: (path: string) => void;
  /** Paths the message being written already references. */
  referenced: Set<string>;
  onError: (message: string) => void;
}

/** A word on a tile, like the PDF badge in Files: legible where a glyph is not. */
const BADGE: Partial<Record<ArtifactKind, string>> = {
  html: 'HTML',
  markdown: 'MD',
  pdf: 'PDF',
  text: 'TXT',
};

export function Tile({ roomId, artifact }: { roomId: string; artifact: Artifact }) {
  const latest = artifact.versions.at(-1)!;
  if (artifact.kind === 'image') {
    return (
      <span className="file__preview file__preview--image">
        <img src={artifactUrl(roomId, artifact.id, latest.version, true)} alt="" loading="lazy" decoding="async" />
      </span>
    );
  }
  const label =
    BADGE[artifact.kind] ?? (artifact.kind === 'code' ? extensionOf(artifact.path).slice(0, 4).toUpperCase() : '');
  return (
    <span className={`file__preview artifact-tile artifact-tile--${artifact.kind}`}>
      {label ? <span className="file__badge">{label}</span> : <FileIcon size={18} />}
    </span>
  );
}

/**
 * Asks, then deletes. Resolves true once the artifact is gone.
 *
 * @param roomName  named in the question when it is not the room on screen
 */
export function useRemoveArtifact(onError: (message: string) => void) {
  const confirm = useConfirm();
  return useCallback(
    async (roomId: string, artifact: Artifact, roomName?: string): Promise<boolean> => {
      const ok = await confirm({
        tone: 'danger',
        title: `Remove “${artifact.title}” from Artifacts?`,
        body:
          `All of its versions go, for everyone in ${roomName ? `“${roomName}”` : 'this room'}. ` +
          'The working file it was published from stays where it is.',
        confirmLabel: 'Remove artifact',
      });
      if (!ok) return false;
      const r = await fetch(
        `/api/rooms/${encodeURIComponent(roomId)}/artifacts/${encodeURIComponent(artifact.id)}`,
        { method: 'DELETE', credentials: 'same-origin' },
      ).catch(() => undefined);
      if (!r?.ok) {
        onError(`Could not delete ${artifact.title}.`);
        return false;
      }
      return true;
    },
    [confirm, onError],
  );
}

/**
 * The Artifacts tab: what Claude handed over, newest first. Opening one turns
 * the drawer into a viewer; the list is a click on "back" away.
 */
export function ArtifactsPanel({ roomId, artifacts, view, onView, onReference, referenced, onError }: Props) {
  const open = view && artifacts.find((a) => a.id === view.id);
  const removeArtifact = useRemoveArtifact(onError);

  async function remove(artifact: Artifact) {
    if ((await removeArtifact(roomId, artifact)) && view?.id === artifact.id) onView(undefined);
  }

  if (open) {
    return (
      <ArtifactViewer
        roomId={roomId}
        artifact={open}
        version={view.version}
        onVersion={(version) => onView({ id: open.id, version })}
        onBack={() => onView(undefined)}
        onReference={() => onReference(open.path)}
        inMessage={referenced.has(open.path)}
        onDelete={() => void remove(open)}
      />
    );
  }

  return (
    <>
      <p className="files__sub">
        Files Claude published for this room. Ask Claude for a report, a page or a diagram and it lands
        here, with every version kept.
      </p>
      <div className="files__list">
        {artifacts.length === 0 && <p className="files__empty">Nothing published yet.</p>}
        {artifacts.map((a) => {
          const latest = a.versions.at(-1)!;
          const inMessage = referenced.has(a.path);
          return (
            <div className={`file ${inMessage ? 'file--referenced' : ''}`} key={a.id}>
              <button className="file__main" onClick={() => onView({ id: a.id })} title={`Open ${a.title}`}>
                <Tile roomId={roomId} artifact={a} />
                <span className="file__text">
                  <span className="file__name-row">
                    <span className="file__name">{a.title}</span>
                    {inMessage && <span className="file__tag">In message</span>}
                  </span>
                  <span className="file__meta">
                    {a.versions.length > 1 && <span className="artifact-version">v{latest.version}</span>}
                    {a.path} · {a.publishedBy ? `${a.publishedBy.name} · ` : ''}{ago(a.updatedAt)}
                  </span>
                </span>
              </button>
              <button
                className="file__action"
                onClick={() => onReference(a.path)}
                aria-label={`Reference ${a.title} in message`}
                title={inMessage ? `Already in your message as ${fileRef(a.path)}` : `Reference in message: ${fileRef(a.path)}`}
              >
                <AtIcon />
              </button>
              <a
                className="file__action"
                href={artifactUrl(roomId, a.id, latest.version, false)}
                download
                aria-label={`Download ${a.title}`}
                title="Download"
              >
                <DownloadIcon />
              </a>
              <button
                className="file__action file__action--danger"
                onClick={() => void remove(a)}
                aria-label={`Delete ${a.title}`}
                title="Remove from Artifacts"
              >
                <TrashIcon />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

interface ViewerProps {
  roomId: string;
  artifact: Artifact;
  version?: number;
  onVersion: (version: number) => void;
  onBack: () => void;
  /** Absent outside the artifact's own room: there is no message to reference it in. */
  onReference?: () => void;
  inMessage?: boolean;
  /** Seen from outside the room: which room it is in, and a way there. */
  room?: { name: string; onGo: () => void };
  onDelete: () => void;
}

export function ArtifactViewer({
  roomId,
  artifact,
  version,
  onVersion,
  onBack,
  onReference,
  inMessage,
  room,
  onDelete,
}: ViewerProps) {
  const latest = artifact.versions.at(-1)!;
  // A version that was asked for but is gone falls back to the latest.
  const current = artifact.versions.find((v) => v.version === version) ?? latest;
  const inline = artifactUrl(roomId, artifact.id, current.version, true);

  return (
    <div className="viewer">
      <div className="viewer__bar">
        <button className="viewer__back" onClick={onBack} aria-label="Back to all artifacts" title="All artifacts">
          <BackIcon />
        </button>
        <div className="viewer__heading">
          <div className="viewer__title">{artifact.title}</div>
          <div className="viewer__meta">
            {room && `${room.name} · `}
            {artifact.path} · {formatSize(current.size)} · {ago(current.createdAt)}
          </div>
        </div>

        {artifact.versions.length > 1 && (
          <select
            className="viewer__versions"
            value={current.version}
            onChange={(e) => onVersion(Number(e.target.value))}
            aria-label="Version"
          >
            {[...artifact.versions].reverse().map((v) => (
              <option key={v.version} value={v.version}>
                v{v.version}
                {v.version === latest.version ? ' · latest' : ''}
              </option>
            ))}
          </select>
        )}

        {onReference && (
          <button
            className={`file__action viewer__action${inMessage ? ' viewer__action--on' : ''}`}
            onClick={onReference}
            aria-label="Reference in message"
            title={inMessage ? `Already in your message as ${fileRef(artifact.path)}` : `Reference in message: ${fileRef(artifact.path)}`}
          >
            <AtIcon />
          </button>
        )}
        {room && (
          <button
            className="file__action viewer__action"
            onClick={room.onGo}
            aria-label={`Open in ${room.name}`}
            title={`Open in “${room.name}”`}
          >
            <EnterIcon />
          </button>
        )}
        <a
          className="file__action viewer__action"
          href={inline}
          target="_blank"
          rel="noreferrer"
          aria-label="Open in a new tab"
          title="Open in a new tab"
        >
          <OpenIcon />
        </a>
        <a
          className="file__action viewer__action"
          href={artifactUrl(roomId, artifact.id, current.version, false)}
          download
          aria-label="Download"
          title="Download"
        >
          <DownloadIcon />
        </a>
        <button
          className="file__action file__action--danger viewer__action"
          onClick={onDelete}
          aria-label="Remove from Artifacts"
          title="Remove from Artifacts"
        >
          <TrashIcon />
        </button>
      </div>

      {current.note && <div className="viewer__note">v{current.version}: {current.note}</div>}

      <div className={`viewer__body viewer__body--${artifact.kind}`}>
        <Preview key={inline} kind={artifact.kind} url={inline} path={artifact.path} title={artifact.title} />
      </div>
    </div>
  );
}

/** Past this, text is shown truncated; the download still has all of it. */
const MAX_TEXT = 1_000_000;

function Preview({ kind, url, path, title }: { kind: ArtifactKind; url: string; path: string; title: string }) {
  switch (kind) {
    case 'html':
      // The server sends the same sandbox as a CSP header; this attribute is
      // the belt to those braces. Never add allow-same-origin here.
      return (
        <iframe
          className="viewer__frame"
          src={url}
          title={title}
          sandbox="allow-scripts allow-popups allow-forms allow-modals"
        />
      );
    case 'pdf':
      return <iframe className="viewer__frame" src={url} title={title} />;
    case 'image':
      return <img className="viewer__image" src={url} alt={title} />;
    case 'markdown':
    case 'code':
    case 'text':
      return <TextPreview kind={kind} url={url} path={path} />;
    default:
      return (
        <div className="viewer__empty">
          <FileIcon size={28} />
          <p>No preview for this kind of file.</p>
          <a className="viewer__download" href={url.replace(/[?&]inline$/, '')} download>
            Download
          </a>
        </div>
      );
  }
}

function TextPreview({ kind, url, path }: { kind: 'markdown' | 'code' | 'text'; url: string; path: string }) {
  const [text, setText] = useState<string | undefined>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => !cancelled && setText(t))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (failed) return <p className="viewer__empty">Could not load this version.</p>;
  if (text === undefined) return <p className="viewer__empty">Loading…</p>;

  const clipped = text.length > MAX_TEXT;
  const body = clipped ? text.slice(0, MAX_TEXT) : text;

  if (kind === 'markdown') {
    return (
      <div className="viewer__doc">
        <Markdown>{body}</Markdown>
      </div>
    );
  }
  if (kind === 'code') {
    // Through the markdown renderer to reuse its highlighting. The fence is
    // longer than any backtick run in the file, so the file cannot close it.
    const longest = Math.max(2, ...[...body.matchAll(/`+/g)].map((m) => m[0].length));
    const fence = '`'.repeat(longest + 1);
    return (
      <div className="viewer__code">
        <Markdown>{`${fence}${extensionOf(path)}\n${body}\n${fence}`}</Markdown>
      </div>
    );
  }
  return (
    <pre className="viewer__text">
      {body}
      {clipped && '\n\n… truncated; download the file for the rest.'}
    </pre>
  );
}
